import { useState } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent,
	DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDistanceToNow } from 'date-fns'
import {
	MoreVertical,
	Camera,
	AlertTriangle,
	Maximize2,
	Lock,
	Contact,
	ChevronUp,
	ChevronDown,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import { getAuthHeaders } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'
import { useProgramContext } from '@/context/ProgramContext'
import { canDeleteFeedback } from '@/lib/programPermissions'
import { Card, CardContent } from '@/components/ui/card'
import { useTranslation } from '@/hooks/useTranslation'

interface ContactMessage {
	id: string
	description: string
	status: string
	severity: string
	created_at: string
	updated_at: string
	screenshot_url: string | null
	user_metadata: {
		browser?: string
		os?: string
		device?: string
		url?: string
		screenSize?: string
	} | null
	reporter_email: string | null
	reporter_name: string | null
	assignee_user_id: string | null
	widget_id: string
	program_id: string
	widget?: {
		name: string
	}
	program?: {
		name: string
	}
}

interface ContactMessageTableProps {
	contacts: ContactMessage[]
	isLoading: boolean
	className?: string
	onContactSelect?: (contactId: string | null) => void
	isPro?: boolean
	hiddenCount?: number
	itemsPerPage?: number
	sort?: string
	onSortChange?: (sort: string) => void
}

type ContactStatus = 'new' | 'in_progress' | 'resolved' | 'closed' | 'archived'

type ContactSeverity = 'critical' | 'high' | 'medium' | 'low'

const getStatusColorClasses = (status: string) => {
	switch (status?.toLowerCase()) {
		case 'new':
			return {
				bg: 'bg-primary/10 dark:bg-primary/20',
				text: 'text-primary dark:text-primary',
				border: 'border-primary/30 dark:border-primary/40',
			}
		case 'in_progress':
			return {
				bg: 'bg-yellow-100 dark:bg-yellow-900/50',
				text: 'text-yellow-700 dark:text-yellow-300',
				border: 'border-yellow-300 dark:border-yellow-700',
			}
		case 'resolved':
			return {
				bg: 'bg-green-100 dark:bg-green-900/50',
				text: 'text-green-700 dark:text-green-300',
				border: 'border-green-300 dark:border-green-700',
			}
		case 'closed':
			return {
				bg: 'bg-slate-100 dark:bg-slate-700/50',
				text: 'text-slate-600 dark:text-slate-300',
				border: 'border-slate-300 dark:border-slate-600',
			}
		case 'archived':
			return {
				bg: 'bg-purple-100 dark:bg-purple-900/50',
				text: 'text-purple-700 dark:text-purple-300',
				border: 'border-purple-300 dark:border-purple-700',
			}
		default:
			return {
				bg: 'bg-gray-100 dark:bg-gray-800/50',
				text: 'text-gray-700 dark:text-gray-400',
				border: 'border-gray-300 dark:border-gray-600',
			}
	}
}

const getSeverityColorClasses = (severity: string) => {
	switch (severity?.toLowerCase()) {
		case 'critical':
			return {
				bg: 'bg-red-100 dark:bg-red-900/50',
				text: 'text-red-700 dark:text-red-300',
				border: 'border-red-300 dark:border-red-700',
			}
		case 'high':
			return {
				bg: 'bg-orange-100 dark:bg-orange-900/50',
				text: 'text-orange-700 dark:text-orange-300',
				border: 'border-orange-300 dark:border-orange-700',
			}
		case 'medium':
			return {
				bg: 'bg-amber-100 dark:bg-amber-900/50',
				text: 'text-amber-700 dark:text-amber-300',
				border: 'border-amber-300 dark:border-amber-700',
			}
		case 'low':
			return {
				bg: 'bg-lime-100 dark:bg-lime-900/50',
				text: 'text-lime-700 dark:text-lime-300',
				border: 'border-lime-300 dark:border-lime-700',
			}
		default:
			return {
				bg: 'bg-gray-100 dark:bg-gray-800/50',
				text: 'text-gray-700 dark:text-gray-400',
				border: 'border-gray-300 dark:border-gray-600',
			}
	}
}

interface SortableHeaderProps {
	children: React.ReactNode
	sortKey: string
	currentSort?: string
	onSortChange?: (sort: string) => void
	className?: string
}

function SortableHeader({
	children,
	sortKey,
	currentSort,
	onSortChange,
	className,
}: SortableHeaderProps) {
	const isActive = currentSort?.startsWith(sortKey)
	const isDesc = currentSort === `${sortKey}_desc`

	const handleClick = () => {
		if (!onSortChange) return

		if (!isActive) {
			onSortChange(`${sortKey}_desc`)
		} else if (isDesc) {
			onSortChange(`${sortKey}_asc`)
		} else {
			onSortChange(`${sortKey}_desc`)
		}
	}

	return (
		<TableHead
			className={cn(
				'cursor-pointer hover:bg-muted/50 transition-colors select-none',
				className
			)}
			onClick={handleClick}>
			<div className="flex items-center gap-1">
				{children}
				<div className="flex flex-col">
					<ChevronUp
						className={cn(
							'h-3 w-3 transition-colors',
							isActive && !isDesc ? 'text-primary' : 'text-muted-foreground/50'
						)}
					/>
					<ChevronDown
						className={cn(
							'h-3 w-3 -mt-1 transition-colors',
							isActive && isDesc ? 'text-primary' : 'text-muted-foreground/50'
						)}
					/>
				</div>
			</div>
		</TableHead>
	)
}

const getStatusLabel = (status: string, t: (key: string) => string): string => {
	switch (status?.toLowerCase()) {
		case 'new':
			return t('contactMessageTable.statusNew')
		case 'in_progress':
			return t('contactMessageTable.statusInProgress')
		case 'resolved':
			return t('contactMessageTable.statusResolved')
		case 'closed':
			return t('contactMessageTable.statusClosed')
		case 'archived':
			return t('contactMessageTable.statusArchived')
		default:
			return status.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.substring(1)).join(' ')
	}
}

const getSeverityLabel = (severity: string, t: (key: string) => string): string => {
	switch (severity?.toLowerCase()) {
		case 'critical':
			return t('contactMessageTable.severityCritical')
		case 'high':
			return t('contactMessageTable.severityHigh')
		case 'medium':
			return t('contactMessageTable.severityMedium')
		case 'low':
			return t('contactMessageTable.severityLow')
		default:
			return severity.charAt(0).toUpperCase() + severity.substring(1)
	}
}

export function ContactMessageTable({
	contacts,
	isLoading,
	className,
	onContactSelect,
	isPro = true,
	hiddenCount = 0,
	itemsPerPage = 10,
	sort,
	onSortChange,
}: ContactMessageTableProps) {
	const { t } = useTranslation()
	const [contactToDelete, setContactToDelete] = useState<string | null>(null)
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { currentProgramAccessLevel } = useProgramContext()
	const canDelete = canDeleteFeedback(currentProgramAccessLevel)

	const deleteContactMutation = useMutation({
		mutationFn: async (contactId: string) => {
			const headers = await getAuthHeaders()
			const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}`, {
				method: 'DELETE',
				headers,
			})
			
			if (!response.ok) {
				const error = await response.json().catch(() => ({ error: 'Failed to delete contact report' }))
				throw new Error(error.error || 'Failed to delete contact report')
			}
			
			return contactId
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['contacts'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData'] })
		},
		onError: (error) => {
			console.error('Error deleting contact:', error)
		},
	})

	const updateContactMutation = useMutation({
		mutationFn: async ({
			contactId,
			updates,
		}: {
			contactId: string
			updates: Partial<ContactMessage> & {
					status?: ContactStatus
					severity?: ContactSeverity
				}
			}) => {
				const headers = await getAuthHeaders()
				const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}`, {
					method: 'PUT',
					headers: {
						'Content-Type': 'application/json',
						...headers,
					},
					body: JSON.stringify(updates),
				})
				if (!response.ok) {
					const error = await response.json().catch(() => ({ error: 'Failed to update contact report' }))
					throw new Error(error.error || 'Failed to update contact report')
				}
				return response.json()
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['contacts'] })
			},
		onError: (error) => {
			console.error('Error updating contact:', error)
		},
	})

	const confirmDelete = () => {
		if (contactToDelete) {
			deleteContactMutation.mutate(contactToDelete)
			setContactToDelete(null)
		}
	}

	if (isLoading && contacts.length === 0) {
		return (
			<Card className={cn('overflow-hidden bg-white dark:bg-slate-900 border-border/50', className)}>
			<div className="w-full overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t('contactMessageTable.status')}</TableHead>
							<TableHead>{t('contactMessageTable.severity')}</TableHead>
							<TableHead className="w-[300px]">{t('contactMessageTable.description')}</TableHead>
							<TableHead>{t('contactMessageTable.reporter')}</TableHead>
							<TableHead>{t('contactMessageTable.screenshot')}</TableHead>
							<TableHead>{t('contactMessageTable.reported')}</TableHead>
							<TableHead className="w-[50px]"></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{[...Array(5)].map((_, i) => (
							<TableRow key={i} className="border-b dark:border-slate-700/50">
								<TableCell>
									<Skeleton className="h-6 w-20 rounded-full" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-6 w-20 rounded-full" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-5 w-5/6 rounded" />
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<Skeleton className="h-6 w-6 rounded-full" />
										<Skeleton className="h-4 w-24 rounded" />
									</div>
								</TableCell>
								<TableCell>
									<Skeleton className="h-8 w-8 rounded" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-28 rounded" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-8 w-8 rounded" />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			</Card>
		)
	}

	const displayedContacts = contacts || []
	const numberOfBlurredRows =
		!isPro && hiddenCount > 0
			? Math.min(3, hiddenCount) // Always show up to 3 blurred rows when there are hidden contacts
			: 0

	if (displayedContacts.length === 0 && numberOfBlurredRows === 0 && !isLoading) {
		return (
			<Card
				className={cn(
					'bg-white dark:bg-slate-900 border-border/50',
					className
				)}>
				<CardContent>
					<div className="text-center py-12 text-muted-foreground">
						<Contact className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
						<h3 className="text-lg font-semibold">{t('contactMessageTable.noMailbox')}</h3>
						<p className="mt-1 text-sm">
							{t('contactMessageTable.noContactsMatchingFilters')}
						</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<>
			<Card className={cn('overflow-hidden bg-white dark:bg-slate-900 border-border/50 relative', className)}>
			<div className="w-full overflow-x-auto relative">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent border-t-0 bg-slate-50/50 dark:bg-slate-800/50 sticky top-0 z-10">
							<SortableHeader
								sortKey="status"
								currentSort={sort}
								onSortChange={onSortChange}>
								{t('contactMessageTable.status')}
							</SortableHeader>
							<SortableHeader
								sortKey="severity"
								currentSort={sort}
								onSortChange={onSortChange}>
								{t('contactMessageTable.severity')}
							</SortableHeader>
							<TableHead className="min-w-[300px]">{t('contactMessageTable.description')}</TableHead>
							<TableHead className="min-w-[150px]">{t('contactMessageTable.reporter')}</TableHead>
							<TableHead className="min-w-[100px]">{t('contactMessageTable.screenshot')}</TableHead>
							<SortableHeader
								sortKey="created_at"
								currentSort={sort}
								onSortChange={onSortChange}
								className="min-w-[150px]">
								{t('contactMessageTable.reported')}
							</SortableHeader>
							<TableHead className="w-[50px]"></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{!isPro &&
							numberOfBlurredRows > 0 &&
							Array.from({ length: numberOfBlurredRows }).map((_, index) => {
								// Generate realistic placeholder data for hidden contacts (showing newest first)
								const placeholderContacts = [
									{
										severity: 'Critical',
										status: 'New',
										description:
											'Critical payment gateway failure causing transaction errors for premium users.',
										reporter: 'Sarah Johnson',
										created: '2 hours ago',
									},
									{
										severity: 'High',
										status: 'New',
										description:
											'Database connection timeouts affecting user login and registration systems.',
										reporter: 'Mike Chen',
										created: '30 minutes ago',
									},
									{
										severity: 'Critical',
										status: 'New',
										description:
											'Security vulnerability in authentication system discovered by penetration testing.',
										reporter: 'Emily Davis',
										created: '1 hour ago',
									},
									{
										severity: 'High',
										status: 'New',
										description:
											'API endpoints returning 500 errors for mobile app users during peak hours.',
										reporter: 'Alex Rodriguez',
										created: '45 minutes ago',
									},
									{
										severity: 'Medium',
										status: 'New',
										description:
											'Email notifications not being sent for password reset requests from users.',
										reporter: 'Jennifer Liu',
										created: '1.5 hours ago',
									},
								]

								const placeholder =
									placeholderContacts[index % placeholderContacts.length]

								return (
									<TableRow
										key={`blurred-contact-${index}`}
										className="relative pointer-events-none select-none filter blur-[3px] opacity-30">
										<TableCell className="w-16">
											<Badge
												variant={
													placeholder.severity === 'Critical'
														? 'destructive'
														: placeholder.severity === 'High'
														? 'destructive'
														: placeholder.severity === 'Medium'
														? 'default'
														: 'secondary'
												}
												className="text-xs">
												{placeholder.severity}
											</Badge>
										</TableCell>
										<TableCell className="w-20">
											<Badge variant="outline" className="text-xs">
												{placeholder.status}
											</Badge>
										</TableCell>
										<TableCell className="max-w-md">
											<div className="space-y-1">
												<p className="text-sm font-medium line-clamp-2">
													{placeholder.description}
												</p>
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Avatar className="h-7 w-7">
													<AvatarFallback className="text-xs bg-muted">
														{placeholder.reporter
															.split(' ')
															.map((n) => n[0])
															.join('')}
													</AvatarFallback>
												</Avatar>
												<span className="text-sm text-muted-foreground">
													{placeholder.reporter}
												</span>
											</div>
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{placeholder.created}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{placeholder.created}
										</TableCell>
										<TableCell>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" className="h-8 w-8 p-0">
														<MoreVertical className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								)
							})}
						{displayedContacts.map((contact) => {
							const statusColors = getStatusColorClasses(contact.status)
							const severityColors = getSeverityColorClasses(contact.severity)
							return (
								<TableRow
									key={contact.id}
									className="cursor-pointer hover:bg-muted/50 transition-colors"
									onClick={(e) => {
										const target = e.target as HTMLElement
										if (
											target.closest('.actions-dropdown-trigger') ||
											target.closest('.screenshot-dialog-trigger') ||
											target.closest('[role="dialog"]') ||
											target.closest('button') ||
											target.closest('[data-radix-collection-item]')
										) {
											return
										}
										onContactSelect?.(contact.id)
									}}>
									<TableCell>
										<Badge
											variant="outline"
											className={cn(
												'capitalize font-normal text-xs px-2 py-0.5',
												statusColors.bg,
												statusColors.text,
												statusColors.border
											)}>
											{getStatusLabel(contact.status, t)}
										</Badge>
									</TableCell>
									<TableCell>
										<Badge
											variant="outline"
											className={cn(
												'capitalize font-normal text-xs px-2 py-0.5',
												severityColors.bg,
												severityColors.text,
												severityColors.border
											)}>
											{getSeverityLabel(contact.severity, t)}
										</Badge>
									</TableCell>
									<TableCell className="font-medium text-sm">
										{contact.description.length > 170
											? `${contact.description.slice(0, 170)}...`
											: contact.description}
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-2">
											<Avatar className="h-7 w-7">
												<AvatarFallback className="text-xs">
													{(contact.reporter_name || contact.reporter_email || 'A')
														.charAt(0)
														.toUpperCase()}
												</AvatarFallback>
											</Avatar>
											<span className="text-xs truncate max-w-[120px] text-muted-foreground group-hover:text-foreground">
												{contact.reporter_name || contact.reporter_email || t('contactMessageTable.anonymous')}
											</span>
										</div>
									</TableCell>
									<TableCell>
										{contact.screenshot_url ? (
											<Dialog>
												<DialogTrigger asChild>
													<div className="screenshot-dialog-trigger group cursor-pointer">
														<img
															src={contact.screenshot_url}
															alt={t('contactMessageTable.contactScreenshot')}
															className="h-12 w-16 object-cover rounded border border-border/50 hover:border-primary/50 transition-colors"
															onClick={(e) => {
																e.stopPropagation()
															}}
														/>
													</div>
												</DialogTrigger>
												<DialogContent className="max-w-[90vw] max-h-[90vh] w-screen h-screen p-0 bg-black/95 border-none shadow-none flex items-center justify-center [&_button_svg]:text-white [&_button]:absolute [&_button]:top-4 [&_button]:right-4 [&_button]:z-50">
													<img
														src={contact.screenshot_url}
														alt={t('contactMessageTable.contactScreenshot')}
														className="max-w-full max-h-full object-contain"
													/>
												</DialogContent>
											</Dialog>
										) : (
											<span className="text-muted-foreground text-xs italic">
												{t('contactMessageTable.none')}
											</span>
										)}
									</TableCell>
									<TableCell className="text-muted-foreground text-xs">
										{formatDistanceToNow(new Date(contact.created_at), {
											addSuffix: true,
										})}
									</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger
												asChild
												className="actions-dropdown-trigger">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													onClick={(e) => e.stopPropagation()}>
													<MoreVertical className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation()
														onContactSelect?.(contact.id)
													}}>
													{t('contactMessageTable.viewDetails')}
												</DropdownMenuItem>
												<DropdownMenuSub>
													<DropdownMenuSubTrigger>
														{t('contactMessageTable.changeStatus')}
													</DropdownMenuSubTrigger>
													<DropdownMenuSubContent>
														{(
															[
																'new',
																'in_progress',
																'resolved',
																'closed',
																'archived',
															] as ContactStatus[]
														).map((st) => (
															<DropdownMenuItem
																key={st}
																onClick={(e) => {
																	e.stopPropagation()
																	updateContactMutation.mutate({
																		contactId: contact.id,
																		updates: { status: st },
																	})
																}}
																disabled={
																	contact.status === st ||
																	updateContactMutation.isPending
																}>
																{getStatusLabel(st, t)}
															</DropdownMenuItem>
														))}
													</DropdownMenuSubContent>
												</DropdownMenuSub>
												<DropdownMenuSub>
													<DropdownMenuSubTrigger>
														{t('contactMessageTable.changeSeverity')}
													</DropdownMenuSubTrigger>
													<DropdownMenuSubContent>
														{(
															[
																'critical',
																'high',
																'medium',
																'low',
															] as ContactSeverity[]
														).map((sev) => (
															<DropdownMenuItem
																key={sev}
																onClick={(e) => {
																	e.stopPropagation()
																	updateContactMutation.mutate({
																		contactId: contact.id,
																		updates: { severity: sev },
																	})
																}}
																disabled={
																	contact.severity === sev ||
																	updateContactMutation.isPending
																}>
																{getSeverityLabel(sev, t)}
															</DropdownMenuItem>
														))}
													</DropdownMenuSubContent>
												</DropdownMenuSub>
												{canDelete && (
													<>
														<DropdownMenuSeparator />
														<DropdownMenuItem
															className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-700/20 dark:focus:text-red-500"
															onClick={(e) => {
																e.stopPropagation()
																setContactToDelete(contact.id)
															}}
															disabled={deleteContactMutation.isPending}>
															{t('contactMessageTable.deleteReport')}
														</DropdownMenuItem>
													</>
												)}
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>

				{/* Upgrade Button Overlay - positioned in center of blurred rows */}
				{!isPro && numberOfBlurredRows > 0 && (
					<div
						className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-30"
						style={{
							top: `${66 + (numberOfBlurredRows * 65) / 2 - 25}px`, // 66px for header + half of blurred rows - half button height
							height: '50px',
						}}>
						<div className="pointer-events-auto">
							<Button
								variant="default"
								size="lg"
								className="upgrade-button shadow-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold animate-pulse border-2 border-primary-foreground/20"
								onClick={(e) => {
									e.stopPropagation()
									navigate('/settings?tab=billing')
								}}>
								<Lock className="h-5 w-5 mr-2.5" />
								{t('contactMessageTable.upgradeToView')}{' '}
								{hiddenCount > 1
									? `${hiddenCount} ${t('contactMessageTable.moreMailbox')}`
									: t('contactMessageTable.moreMailbox')}
							</Button>
						</div>
					</div>
				)}
			</div>
			</Card>
			<AlertDialog
				open={!!contactToDelete}
				onOpenChange={() => setContactToDelete(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('contactMessageTable.areYouSure')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('contactMessageTable.deleteContactConfirmation')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteContactMutation.isPending}>
							{t('contactMessageTable.cancel')}
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white"
							onClick={confirmDelete}
							disabled={deleteContactMutation.isPending}>
							{deleteContactMutation.isPending ? t('contactMessageTable.deleting') : t('contactMessageTable.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
